// ============================================
// @gs/ui — GsIconButton
// Bouton icône minimaliste avec tooltip intégré
// Standard GS : pas de label visible, tooltip au hover
// ============================================

import { type ReactNode, type ButtonHTMLAttributes } from 'react';

export interface GsIconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Icône Lucide à afficher (ex: <FileDown size={15} />) */
  icon: ReactNode;
  /** Texte du tooltip affiché au hover */
  tooltip: string;
  /** Variante visuelle */
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  /** Taille du bouton */
  size?: 'sm' | 'md' | 'lg';
  /** Position du tooltip */
  tooltipSide?: 'top' | 'bottom';
}

const VARIANT_CLASSES: Record<string, string> = {
  primary:   'bg-brand-500 hover:bg-brand-600 active:bg-brand-700 text-white shadow-sm',
  secondary: 'bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 active:bg-slate-200 dark:active:bg-slate-600 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 shadow-sm',
  ghost:     'hover:bg-slate-100 dark:hover:bg-slate-700 active:bg-slate-200 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200',
  danger:    'bg-red-500 hover:bg-red-600 active:bg-red-700 text-white shadow-sm',
};

const SIZE_CLASSES: Record<string, string> = {
  sm: 'w-7 h-7',
  md: 'w-8 h-8',
  lg: 'w-9 h-9',
};

export function GsIconButton({
  icon,
  tooltip,
  variant = 'secondary',
  size = 'md',
  tooltipSide = 'top',
  className = '',
  ...props
}: GsIconButtonProps) {
  const isTop = tooltipSide === 'top';

  return (
    <div className="relative group inline-flex">
      <button
        type="button"
        className={[
          'inline-flex items-center justify-center rounded-lg',
          'transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/30',
          'disabled:opacity-40 disabled:pointer-events-none',
          SIZE_CLASSES[size],
          VARIANT_CLASSES[variant],
          className,
        ].join(' ')}
        {...props}
      >
        {icon}
      </button>

      {/* Tooltip */}
      <div
        className={[
          'pointer-events-none absolute left-1/2 -translate-x-1/2 z-50',
          'whitespace-nowrap rounded-md bg-slate-800 px-2 py-1',
          'text-meta font-medium text-white shadow-lg',
          'opacity-0 group-hover:opacity-100 transition-opacity duration-150',
          isTop ? 'bottom-full mb-2' : 'top-full mt-2',
        ].join(' ')}
      >
        {tooltip}
        {/* Flèche */}
        <div
          className={[
            'absolute left-1/2 -translate-x-1/2 border-4 border-transparent',
            isTop
              ? 'top-full border-t-slate-800'
              : 'bottom-full border-b-slate-800',
          ].join(' ')}
        />
      </div>
    </div>
  );
}
